import React, { useEffect, useState } from 'react';
// import { useTheme } from 'styled-components/native';
import { Modal, Platform, Pressable } from 'react-native';
import { Modalize } from 'react-native-modalize';
// import { SvgProps } from 'react-native-svg';

import Content from '../Content';
import Button from '../Button';
import { HeaderBottomSheet, TitleText, BodyText, ButtonIcon, IconFA, WebOverlay, WebSheet } from './styles';

// import IconClose from '../../assets/icons/close.svg';
// import { getBottomSpacing } from '../../utils/functions';
import { themes } from '../../design/colors';

interface BottomSheetProps {
  bottomSheetRef: any;
  title: string;
  description?: string;
  textButton?: string;
  iconButton?: any;
  typeButton?: 'outlined' | 'filled' | 'text' | 'error';
  disabledButton?: boolean;
  accessibilityHintButton?: string;
  textButtonSecondary?: string;
  iconButtonSecondary?: any;
  typeButtonSecondary?: 'outlined' | 'filled' | 'text' | 'error';
  disabledButtonSecondary?: boolean;
  accessibilityHintButtonSecondary?: string;
  children?: any;
  alignmentTitle?: 'center' | 'left';
  loading?: boolean;
  loadingSecondary?: boolean;
  onClose: () => void;
  onPress?: () => void;
  onPressSecondary?: () => void;
}

/**
 *  Componente ButtonSheet.
 *  - `bottomSheetRef`: Faz referencia ao componente do bottomSheet
 *  - `title`: Texto exibido no topo do bottomSheet
 *  - `description`: Texto que descreve sobre o que se trata o bottomSheet
 *  - `textButton`: Texto do botão
 *  - `iconButton?`: Opção de botão com ícone
 *  - `typeButton?`: Tipo do botão de acordo com o Design System (default: `filled`)
 *  - `typeButtonSecondary?`: Tipo do botão secundário de acordo com o Design System (default: `filled`)
 *  - `textButtonSecondary?`: Texto do botão secundário
 *  - `iconButtonSecondary?`: Opção de botão secundário com ícone
 *  - `disabledButton?`: Desabilitar o botão, altera as cores e impede o clique
 *  - `disabledButtonSecondary?`: Desabilitar o botão secundário, altera as cores e impede o clique
 *  - `accessibilityHintButton?`: Texto para dica de acessibilidade do botão
 *  - `accessibilityHintButtonSecondary?`: Texto para dica de acessibilidade do botão secundário
 *  - `onClose`: Função para fechar o bottomSheet
 *  - `onPress`: Função realizada ao pressionar o botão
 *  - `onPressSecondary`: Função realizada ao pressionar o botão secundário
 *  - `children`: Recebe o JSX criado a tela que o componente é chamado
 *  - `alignmentTitle`: Define o alinhamento do título (centralizado ou para a esquerta)
 *  - 'loading?': Feedback de carregamento
 *  - 'loadingSecondary?': Feedback de carregamento do botão secundário
 */
export default function BottomSheet({
  bottomSheetRef,
  title,
  description,
  textButton,
  iconButton,
  typeButton = 'filled',
  disabledButton,
  accessibilityHintButton,
  textButtonSecondary,
  iconButtonSecondary,
  typeButtonSecondary,
  disabledButtonSecondary,
  accessibilityHintButtonSecondary,
  children,
  alignmentTitle,
  loading,
  loadingSecondary,
  onClose,
  onPress,
  onPressSecondary,
}: BottomSheetProps) {
  // const theme = useTheme();

  const sheetContent = (
    <Content>
      <HeaderBottomSheet>
        <ButtonIcon
          accessible
          accessibilityLabel="Fechar janela"
          accessibilityRole="button"
          accessibilityHint="Fechar a janela de informações"
          onPress={onClose}
        >
          <IconFA name='x' size={25} color={themes.colors.primary} />
        </ButtonIcon>
      </HeaderBottomSheet>
      <TitleText accessible>
        {title}
      </TitleText>
      {description ? <BodyText accessible>{description}</BodyText> : children}

      {textButton && (
        <Button
          title={textButton}
          onPress={onPress}
          // type={typeButton}
          // Icon={iconButton ?? null}
          loading={loading}
          disabled={disabledButton}
          accessible
          accessibilityLabel={textButton}
          accessibilityRole="button"
          accessibilityHint={accessibilityHintButton ?? ''}
          style={{ marginBottom: textButtonSecondary ? 0 : 10 }}
        />
      )}

      {textButtonSecondary && (
        <Button
          title={textButtonSecondary}
          onPress={onPressSecondary}
          // type={typeButtonSecondary}
          // Icon={iconButtonSecondary ?? null}
          loading={loadingSecondary}
          disabled={disabledButtonSecondary}
          accessible
          accessibilityLabel={textButtonSecondary}
          accessibilityRole="button"
          accessibilityHint={accessibilityHintButtonSecondary ?? ''}
          style={{ marginTop: typeButton === 'text' ? 8 : 12, marginBottom: 10 }}
        />
      )}
    </Content>
  );

  // O Modalize depende de measure()/UIManager nativos para calcular a altura
  // do conteúdo e animar a entrada. No react-native-web essa medição falha
  // (fica com altura 0), então o overlay aparece mas o cartão nunca é
  // desenhado. No Web usamos o Modal padrão do RN, que renderiza como um
  // <div> comum e não depende dessas medições. A ref exposta imita a API do
  // Modalize (.open()/.close()) para não exigir mudança nas telas que a usam.
  if (Platform.OS === 'web') {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
      bottomSheetRef.current = {
        open: () => setVisible(true),
        close: () => setVisible(false),
      };
    }, []);

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <WebOverlay>
          <Pressable style={{ flex: 1 }} onPress={onClose} />
          <WebSheet>{sheetContent}</WebSheet>
        </WebOverlay>
      </Modal>
    );
  }

  return (
    <Modalize
      ref={bottomSheetRef}
      adjustToContentHeight
      closeOnOverlayTap
      withHandle={false}
      panGestureEnabled={false}
      modalStyle={{
        backgroundColor: themes.colors.white,
      }}
      childrenStyle={{
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        overflow: 'hidden',
      }}
    >
      {sheetContent}
    </Modalize>
  );
}
